import { BRAND } from "constants/brand";
import { useEffect, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faClose, faTriangleExclamation } from "@fortawesome/free-solid-svg-icons";
import { Form } from "react-bootstrap";
import { Switch } from "@mui/material";
import { CSSTransition } from "react-transition-group";
import { Ring } from "@uiball/loaders";

import { useCityWallet, useSaveCityWallet } from "hooks/useCityWallet";
import "./cityWallet.scss";

/**
 * محفظة السائقين — the per-city driver wallet policy.
 *
 * THE SAME LAYOUT AS «محفظة المطاعم» (CityVendorWallet): the switch, the
 * counts, the warning before a switch goes on, then this side's rate. Each
 * modal owns its own side — the restaurant default rate lives in the
 * restaurant modal (admin#41).
 *
 * ONE RULE, LIKE THE RESTAURANT WALLET. The driver fee is always the platform's
 * share of the delivery fee: delivery fee × the driver's rate. There used to be
 * a «طريقة احتساب رسوم الطلب» choice; no real city used another basis, and the
 * restaurant wallet never had one, so it was removed (admin#41).
 *
 * NOT PART OF THE CITY FORM, on purpose. That form posts multipart and saves
 * through the generic mass-assign PATCH; these columns decide what an order
 * costs a driver, so they have their own validated endpoint.
 *
 * THE WARNING IS THE POINT OF THE SCREEN. Every driver starts at a zero
 * balance. A city that turns the wallet on before it tops anybody up stops its
 * whole fleet, silently. The API reports how many drivers hold no balance, and
 * the switch asks for a deliberate confirmation before it goes on.
 */
const CityWallet = (props) => {
    const nodeRef = useRef();
    const cityId = props.show ? props.region?.id : null;
    const { policy, loading } = useCityWallet(cityId);
    const save = useSaveCityWallet();

    const [enabled, setEnabled] = useState(false);
    const [driverPercentage, setDriverPercentage] = useState("");
    const [understood, setUnderstood] = useState(false);

    useEffect(() => {
        if (!policy) return;

        setEnabled(!!policy.driver_wallet_enabled);
        setDriverPercentage(String(policy.driver_wallet_default_percentage ?? ""));
        setUnderstood(false);
    }, [policy]);

    // Only a change from off to on can strand a driver. Turning it OFF, or
    // editing the rate while it is already on, needs no confirmation.
    const isTurningOn = enabled && policy?.driver_wallet_enabled === false;
    const strandedCount = policy?.emptyWalletCount ?? 0;
    const canSave = !save.isPending && Boolean(policy) && (!isTurningOn || understood);

    const submit = async (event) => {
        event.preventDefault();

        try {
            const result = await save.mutateAsync({
                cityId: props.region.id,
                enabled,
                driverPercentage: Number(driverPercentage),
            });

            props.showMessage(
                "success",
                "تم الحفظ",
                result.driver_wallet_enabled
                    ? `محفظة السائقين مفعلة. ${result.emptyWalletCount} من ${result.driverCount} سائق بدون رصيد ولن يستطيعوا استلام الطلبات.`
                    : "محفظة السائقين غير مفعلة في هذه المدينة.",
            );
            props.close();
        } catch (err) {
            props.showMessage(
                "error",
                "تعذر الحفظ",
                err.response?.data?.message || "حدث خطأ غير متوقع",
            );
        }
    };

    return (
        <div className={`model ${props.show ? "model-show" : ""}`} onClick={props.close}>
            <CSSTransition
                mountOnEnter
                unmountOnExit
                nodeRef={nodeRef}
                in={props.show}
                timeout={400}
                classNames="show"
            >
                <div
                    className="addItem city-wallet"
                    ref={nodeRef}
                    onClick={(e) => e.stopPropagation()}
                >
                    <div className="head center">
                        <h5>محفظة السائقين — {props.region?.name}</h5>
                        <FontAwesomeIcon icon={faClose} onClick={props.close} />
                    </div>

                    {loading ? (
                        <div className="loading-spinner center">
                            <Ring size={40} lineWeight={5} speed={2} color={BRAND} />
                        </div>
                    ) : (
                        <Form className="form" onSubmit={submit}>
                            <div className="city-wallet__switch surface-card">
                                <div>
                                    <strong>تفعيل محفظة السائقين في هذه المدينة</strong>
                                    <p className="city-wallet__switch-hint">
                                        عند التفعيل، يدفع السائق رسوم الطلب من رصيده قبل استلامه.
                                        يُطبق فقط على السائقين المفعّل لهم نظام المحفظة.
                                    </p>
                                </div>
                                <Switch
                                    checked={enabled}
                                    onChange={(e) => setEnabled(e.target.checked)}
                                />
                            </div>

                            <p className="city-wallet__counts">
                                {policy?.driverCount ?? 0} سائق في هذه المدينة، منهم {strandedCount}{" "}
                                بدون رصيد.
                            </p>

                            {/* The consequence, with the real number, BEFORE the
                                save. A driver at zero simply stops seeing orders
                                — nothing on this dashboard would say why. */}
                            {isTurningOn ? (
                                <div className="city-wallet__warning">
                                    <FontAwesomeIcon icon={faTriangleExclamation} />
                                    <div>
                                        <strong>
                                            {strandedCount} من {policy?.driverCount ?? 0} سائق في
                                            هذه المدينة بدون رصيد.
                                        </strong>
                                        <p>
                                            بعد التفعيل لن يستطيع أي سائق بدون رصيد استلام أي طلب،
                                            حتى تشحن محفظته. اشحن المحافظ أولاً، ثم فعّل.
                                        </p>
                                        <Form.Check
                                            type="checkbox"
                                            id="city-wallet-understood"
                                            checked={understood}
                                            onChange={(e) => setUnderstood(e.target.checked)}
                                            label="أفهم أن التفعيل يوقف السائقين بدون رصيد"
                                        />
                                    </div>
                                </div>
                            ) : null}

                            <div className="row row-cols-1 row-cols-md-2 g-2">
                                <div className="col">
                                    <Form.Label>نسبة السائق الافتراضية (%)</Form.Label>
                                    <Form.Control
                                        type="number"
                                        min="0"
                                        max="100"
                                        step="0.01"
                                        value={driverPercentage}
                                        onChange={(e) => setDriverPercentage(e.target.value)}
                                    />
                                    <p className="city-wallet__hint">
                                        رسوم الطلب = أجرة التوصيل × نسبة السائق. تُستخدم هذه النسبة
                                        للسائق الذي ليس له نسبة خاصة به.
                                    </p>
                                </div>
                            </div>

                            <div className="buttons">
                                <button className="button" type="submit" disabled={!canSave}>
                                    {save.isPending ? "جاري الحفظ..." : "حفظ الإعدادات"}
                                </button>
                            </div>
                        </Form>
                    )}
                </div>
            </CSSTransition>
        </div>
    );
};

export default CityWallet;
